import { useState } from 'react'
import { 
  Plus, 
  User, 
  Calendar, 
  Dumbbell, 
  Wrench, 
  FileText,
  ChevronDown
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useSessionStore } from '@/stores/sessionStore'
import { useLibraryStore } from '@/stores/libraryStore'

interface QuickActionDropdownProps {
  onAddClient?: () => void
  onScheduleSession?: () => void
  onCreateExercise?: () => void
  onCreateEquipment?: () => void
}

export function QuickActionDropdown({ 
  onAddClient, 
  onScheduleSession,
  onCreateExercise,
  onCreateEquipment 
}: QuickActionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const { openAddSessionModal } = useSessionStore()
  const { 
    setIsCreateExerciseModalOpen, 
    setIsCreateEquipmentModalOpen,
    setActiveTab 
  } = useLibraryStore()

  const quickActions = [
    {
      id: 'add-client',
      label: 'Add Client',
      icon: User,
      onClick: onAddClient || (() => navigate('/clients?action=add')),
      description: 'Add a new client to your roster'
    },
    {
      id: 'schedule-session',
      label: 'Schedule Session',
      icon: Calendar,
      onClick: onScheduleSession || (() => {
        openAddSessionModal()
        navigate('/schedule')
      }),
      description: 'Schedule a training session'
    },
    {
      id: 'create-program',
      label: 'Create Program',
      icon: FileText,
      onClick: () => navigate('/programs/create'),
      description: 'Design a new training program'
    },
    {
      id: 'create-exercise',
      label: 'Create Exercise',
      icon: Dumbbell,
      onClick: onCreateExercise || (() => {
        setActiveTab('exercises')
        setIsCreateExerciseModalOpen(true)
        navigate('/library')
      }),
      description: 'Add exercise to library'
    },
    {
      id: 'create-equipment',
      label: 'Create Equipment',
      icon: Wrench,
      onClick: onCreateEquipment || (() => {
        setActiveTab('equipment')
        setIsCreateEquipmentModalOpen(true)
        navigate('/library')
      }),
      description: 'Add equipment to library'
    }
  ]

  const handleActionClick = (action: typeof quickActions[0]) => {
    action.onClick()
    setIsOpen(false)
  }

  return (
    <div className="relative">
      {/* Quick Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:block">Quick Action</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-3 py-2 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900">Quick Actions</p>
            <p className="text-xs text-gray-500">Common tasks you can perform</p>
          </div>
          
          <div className="py-1">
            {quickActions.map((action) => (
              <button
                key={action.id}
                onClick={() => handleActionClick(action)}
                className="flex items-start w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <action.icon className="w-4 h-4 text-gray-400" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {action.label}
                  </p>
                  <p className="text-xs text-gray-500">
                    {action.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Close dropdown when clicking outside */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}